import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { useUser } from '../hooks/useUsers';
import { updateProfile } from '../data/userService';
import PhotoPicker from '../components/PhotoPicker';
import ProfileCard from '../components/ProfileCard';
import './EditProfilePage.css';

const TAG_OPTIONS = [
  'Long-term', 'ETFs', 'Global', 'Monthly investing', 'Dividends',
  'Individual stocks', 'Tech', 'Property', 'Index funds', 'Learning in public',
];
const BIO_MAX = 160;

/** Create or edit your own profile, with a live preview of what others see. */
export default function EditProfilePage() {
  const { currentUid, refresh } = useSession();
  const { user } = useUser(currentUid);
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [newCred, setNewCred] = useState('');
  const [saving, setSaving] = useState(false);

  // Copy the saved profile into the form once it has loaded.
  useEffect(() => {
    if (user && !form) {
      setForm({
        name: user.name || '',
        location: user.location || '',
        investingSince: user.investingSince || '',
        bio: user.bio || '',
        credentials: [...(user.credentials || [])],
        tags: [...(user.tags || [])],
      });
    }
  }, [user, form]);

  if (!user || !form) return <main className="page"><p className="muted">Loading…</p></main>;

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const addCred = () => {
    const value = newCred.trim();
    if (!value || form.credentials.includes(value)) return;
    setForm({ ...form, credentials: [...form.credentials, value] });
    setNewCred('');
  };
  const removeCred = (c) => setForm({ ...form, credentials: form.credentials.filter((x) => x !== c) });
  const toggleTag = (t) =>
    setForm({ ...form, tags: form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t] });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    await updateProfile(currentUid, { ...form, name: form.name.trim() || user.name });
    refresh();
    navigate(`/profile/${currentUid}`);
  };

  const preview = { ...user, ...form };

  return (
    <main className="page">
      <Link to={`/profile/${currentUid}`} className="back-link">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
        My profile
      </Link>
      <div>
        <h1 className="page-title">Edit profile</h1>
        <p className="muted">This is what other members see when they open your profile.</p>
      </div>

      <div className="edit">
        <form className="card edit__form" onSubmit={save}>
          <div className="edit__photo">
            <PhotoPicker
              uid={currentUid}
              user={{ ...user, ...form }}
              onChange={(url) => { setForm((f) => ({ ...f, photoUrl: url })); refresh(); }}
            />
          </div>

          <div className="edit__row">
            <div className="field edit__grow">
              <label htmlFor="pf-name">Display name</label>
              <input id="pf-name" className="input" value={form.name} onChange={set('name')} required />
            </div>
            <div className="field edit__grow">
              <label htmlFor="pf-loc">Location</label>
              <input id="pf-loc" className="input" value={form.location} onChange={set('location')} />
            </div>
            <div className="field edit__since">
              <label htmlFor="pf-since">Investing since</label>
              <input id="pf-since" className="input" inputMode="numeric" value={form.investingSince} onChange={set('investingSince')} />
            </div>
          </div>

          <div className="field">
            <label htmlFor="pf-bio">Bio</label>
            <textarea id="pf-bio" className="input" rows={3} maxLength={BIO_MAX} value={form.bio} onChange={set('bio')} />
            <span className="edit__count">{form.bio.length}/{BIO_MAX}</span>
          </div>

          <div className="field">
            <span className="field__label">Credentials</span>
            <span className="field__hint">What helps others trust your view: years investing, your job, qualifications, awards.</span>
            <ul className="chips">
              {form.credentials.map((c) => (
                <li key={c} className="chip edit__cred">
                  {c}
                  <button type="button" className="edit__remove" aria-label={`Remove ${c}`} onClick={() => removeCred(c)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                  </button>
                </li>
              ))}
            </ul>
            <div className="edit__add">
              <label htmlFor="pf-cred" className="sr-only">Add a credential</label>
              <input
                id="pf-cred"
                className="input"
                placeholder="e.g. Chartered accountant"
                value={newCred}
                onChange={(e) => setNewCred(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCred(); } }}
              />
              <button type="button" className="btn btn--ghost" onClick={addCred}>Add</button>
            </div>
          </div>

          <fieldset className="field edit__tags">
            <legend className="field__label">Investing style</legend>
            <div className="edit__tag-list">
              {TAG_OPTIONS.map((t) => {
                const on = form.tags.includes(t);
                return (
                  <button key={t} type="button" aria-pressed={on} className={`tag-toggle${on ? ' tag-toggle--on' : ''}`} onClick={() => toggleTag(t)}>
                    {t}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="edit__footer">
            <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</button>
            <Link to={`/profile/${currentUid}`} className="btn btn--ghost">Cancel</Link>
          </div>
        </form>

        <aside className="edit__preview" aria-label="Preview">
          <p className="eyebrow">How others see you</p>
          <ProfileCard user={preview} showStats={false} />
        </aside>
      </div>
    </main>
  );
}
